import React, { useCallback, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Change } from '../models';
import { Button } from './Button';
import SearchApi from '../services/Search';

export type RelatedChangesModalProps = {
    show: boolean;
    current: Change;
    excludeIds?: string[];
    processing?: boolean;
    onClose: () => void;
    onApply: (related: Change) => void | Promise<void>;
};

function RelatedChangesModal(props: RelatedChangesModalProps) {
    const {
        show,
        current,
        excludeIds = [],
        processing = false,
        onClose,
        onApply,
    } = props;
    const { t, i18n } = useTranslation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Change[]>([]);
    const [searching, setSearching] = useState(false);
    const [target, setTarget] = useState<Change | null>(null);

    const blockedIds = useMemo(
        () => new Set([current._id, ...excludeIds]),
        [current._id, excludeIds],
    );

    const doSearch = useCallback(
        _.debounce(async (q: string) => {
            if (q.trim().length < 2) {
                setResults([]);
                return;
            }
            setSearching(true);
            try {
                const res = await SearchApi.search(q.trim(), i18n.language);
                const filtered = (res.changes || []).filter(
                    (item: Change) => !blockedIds.has(item._id) && !item.isMerged(),
                );
                setResults(filtered);
            } finally {
                setSearching(false);
            }
        }, 350),
        [blockedIds, i18n.language],
    );

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const q = e.target.value;
        setQuery(q);
        setTarget(null);
        doSearch(q);
    };

    const handleClose = () => {
        setQuery('');
        setResults([]);
        setTarget(null);
        onClose();
    };

    const modal = (
        <Modal show={show} size="2xl" onClose={handleClose} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Modal.Header>{t('changes.related_link_title')}</Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('changes.related_link_hint', {
                            slug: current.slugName(),
                        })}
                    </p>

                    <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t('changes.related_search')}
                        </div>
                        <div className="relative">
                            <input
                                type="text"
                                autoFocus
                                value={query}
                                onChange={handleQueryChange}
                                placeholder={t('search.search_input') || ''}
                                className="w-full rounded border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                            />
                            {searching && (
                                <div className="absolute right-3 top-2.5 text-gray-400 text-xs">{t('common.loading') || '…'}</div>
                            )}
                        </div>

                        {results.length > 0 && !target && (
                            <div className="mt-1 max-h-60 overflow-y-auto rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md">
                                {results.map((item) => (
                                    <button
                                        key={item._id}
                                        type="button"
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-950/40 border-b border-gray-100 dark:border-gray-700 last:border-0 flex items-center gap-2"
                                        onClick={() => {
                                            setTarget(item);
                                            setQuery(item.slugName() + ' — ' + item.localizedTitle(i18n.language));
                                            setResults([]);
                                        }}
                                    >
                                        <span className="shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-800">
                                            {item.slugName()}
                                        </span>
                                        <span className="truncate text-gray-800 dark:text-gray-100">{item.localizedTitle(i18n.language)}</span>
                                        {item.project?.name && (
                                            <span className="ml-auto shrink-0 text-[11px] text-gray-400">{item.project.name}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}

                        {query.trim().length >= 2 && !searching && results.length === 0 && !target && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('search.no_result')}</p>
                        )}
                    </div>

                    {target && (
                        <div className="rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-3 py-2 text-sm text-blue-900 flex items-center justify-between">
                            <span>
                                <strong>{target.slugName()}</strong>
                                {' — '}
                                {target.localizedTitle(i18n.language)}
                                {target.project?.name ? ` (${target.project.name})` : ''}
                            </span>
                            <button
                                type="button"
                                className="ml-3 text-xs text-blue-600 hover:underline shrink-0"
                                onClick={() => { setTarget(null); setQuery(''); setResults([]); }}
                            >
                                {t('common.change') || 'Changer'}
                            </button>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            small
                            color="none"
                            title={t('common.cancel') as string}
                            icon="close"
                            onClick={handleClose}
                        />
                        <Button
                            small
                            color="primary"
                            title={t('changes.related_link_apply') as string}
                            icon={processing ? 'loading' : 'branch'}
                            onClick={() => {
                                if (target)
                                    onApply(target);
                            }}
                            disabled={!target || processing}
                        />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );

    return ReactDOM.createPortal(modal, document.body);
}

export default RelatedChangesModal;
